<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.LARGE">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ widget.body?.title }}</template>
            </v-toolbar>
            <v-divider />
            <v-card-text>
                <v-row class="pb-1">
                    <v-col class="py-0" cols="12" lg="8" md="6">
                        <v-btn
                            color="blue-grey"
                            :disabled="disableBackward"
                            icon="mdi-chevron-left"
                            variant="text"
                            @click="previousMonth"
                        />
                        <span class="text-subtitle-2">{{ currentMonthLabel }}</span>
                        <v-btn
                            color="blue-grey"
                            :disabled="disableForward"
                            icon="mdi-chevron-right"
                            variant="text"
                            @click="nextMonth"
                        />
                    </v-col>
                    <v-col class="py-0" cols="12" lg="4" md="6">
                        <v-select
                            v-model="selectedSource"
                            :items="sources"
                            density="compact"
                            hide-details
                            item-title="name"
                            item-value="id"
                            :no-data-text="t('common.dashboard.labels.noPrefixes')"
                            return-object
                            variant="outlined"
                        />
                    </v-col>
                </v-row>

                <v-data-table
                    :headers="headers"
                    :items="currentMonthOrders"
                    density="compact"
                    hide-default-footer
                    item-value="shopProductName"
                />
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { DashboardPermission, DashboardWidget } from '@/stores/dashboard'
import {
    createMonthNavigationState,
    DASHBOARD_WIDGET_COLUMNS,
    type MonthNavigationState,
    moveMonthBack,
    moveMonthForward
} from '@/views/Dashboard/dashboardUtils'

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

// 3. Constants
const headers = [
    { title: 'Produkt', key: 'shopProductName', sortable: false },
    { title: 'Waluta', key: 'currency', sortable: false },
    { title: 'Kwota', key: 'price', sortable: false },
    { title: 'Ilość zamówień', key: 'ordersCount', sortable: false },
    { title: 'Nakład', key: 'qtyTotal', sortable: false }
]

// 4. Stores & top-level services
const { t } = useI18n()

// 5. Reactive state (refs/reactive)
const monthState = ref<MonthNavigationState>(createMonthNavigationState())
const selectedSource = ref<{ id: unknown; name: string } | null>(null)

// 7. Computed
const sources = computed(() => {
    const sourceMap = new Map<string, { id: unknown; name: string }>()
    const content = props.widget?.body?.content
    const rows = Array.isArray(content) ? content : []
    rows.forEach((item: { id?: unknown; name?: string }) => {
        const name = item.name ?? ''
        if (!sourceMap.has(name)) sourceMap.set(name, { id: item.id, name })
    })
    const options = Array.from(sourceMap.values())
    return options
})

const currentMonthLabel = computed(() =>
    monthState.value.currentDate.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })
)

const disableForward = computed(() => monthState.value.targetStep === monthState.value.maxStep)
const disableBackward = computed(() => monthState.value.targetStep === monthState.value.minStep)

const currentMonthOrders = computed(() => {
    if (!selectedSource.value) return []

    const date = monthState.value.currentDate
    const month = date.getMonth()
    const year = date.getFullYear()

    const content = props.widget?.body?.content
    const rows = Array.isArray(content) ? content : []
    return rows.filter((item: { checkDate?: string; date?: string; name?: string }) => {
        const checkDate = new Date(item.checkDate || item.date || '')
        return item.name === selectedSource.value?.name && checkDate.getMonth() === month && checkDate.getFullYear() === year
    })
})

// 8. Handlers / methods
function previousMonth(): void {
    moveMonthBack(monthState.value)
}

function nextMonth(): void {
    moveMonthForward(monthState.value)
}

watch(
    sources,
    (nextSources: { id: unknown; name: string }[]) => {
        if (!selectedSource.value && nextSources.length) {
            selectedSource.value = nextSources.find((item) => item.name === 'Drukomat') || nextSources[0]
        }
    },
    { immediate: true }
)
</script>
